import React from 'react';
import { useAuth } from '../context/AuthContext';
import { t } from '../lib/i18n';
import { Card, CardContent } from '../components/ui/card';
import { PublicLayout } from '../components/landing/PublicLayout';
import { Sparkles, Zap, Bug } from 'lucide-react';

const badgeConfig = {
  new: { icon: Sparkles, color: 'bg-green-500/10 text-green-500 border-green-500/20' },
  improved: { icon: Zap, color: 'bg-blue-500/10 text-blue-500 border-blue-500/20' },
  fixed: { icon: Bug, color: 'bg-amber-500/10 text-amber-500 border-amber-500/20' },
};

function ChangeBadge({ type, language }) {
  const config = badgeConfig[type];
  const Icon = config.icon;
  const label = t(`updates_badge_${type}`, language);
  return (
    <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full border ${config.color}`}>
      <Icon className="h-3 w-3" />
      {label}
    </span>
  );
}

export default function UpdatesPage() {
  const { language } = useAuth();

  const releases = [
    {
      version: '2.4.0',
      date: '2026-03-28',
      title: language === 'fr' ? 'Soundboard & Ambiance Sonore' : 'Soundboard & Sound Ambiance',
      changes: [
        { type: 'new', text: language === 'fr' ? 'Soundboard avec 100+ effets sonores organisés par catégorie' : 'Soundboard with 100+ sound effects organized by category' },
        { type: 'new', text: language === 'fr' ? 'Crossfade automatique entre les playlists' : 'Automatic crossfade between playlists' },
        { type: 'improved', text: language === 'fr' ? 'Lecteur audio global avec contrôle de volume master' : 'Global audio player with master volume control' },
        { type: 'fixed', text: language === 'fr' ? 'Correction du lag audio lors du changement de scène' : 'Fixed audio lag when switching scenes' },
      ],
    },
    {
      version: '2.3.0',
      date: '2026-03-10',
      title: language === 'fr' ? 'Game System Builder' : 'Game System Builder',
      changes: [
        { type: 'new', text: language === 'fr' ? 'Créateur de systèmes de jeu avec 8 types de champs' : 'Game system builder with 8 field types' },
        { type: 'new', text: language === 'fr' ? 'Éditeur PDF visuel drag & drop pour fiches de personnage' : 'Visual drag & drop PDF editor for character sheets' },
        { type: 'improved', text: language === 'fr' ? 'Support des permissions par rôle (MJ / Joueur)' : 'Role-based permissions support (GM / Player)' },
        { type: 'fixed', text: language === 'fr' ? 'Correction de l\'export PDF sur navigateurs mobiles' : 'Fixed PDF export on mobile browsers' },
      ],
    },
    {
      version: '2.2.0',
      date: '2026-02-15',
      title: language === 'fr' ? 'Marketplace de Templates' : 'Template Marketplace',
      changes: [
        { type: 'new', text: language === 'fr' ? 'Marketplace pour parcourir et importer des campagnes prêtes à jouer' : 'Marketplace to browse and import ready-to-play campaigns' },
        { type: 'new', text: language === 'fr' ? 'Tri par difficulté, système de jeu et popularité' : 'Sorting by difficulty, game system, and popularity' },
        { type: 'improved', text: language === 'fr' ? 'Import en un clic avec prévisualisation du contenu' : 'One-click import with content preview' },
      ],
    },
    {
      version: '2.1.0',
      date: '2026-01-20',
      title: language === 'fr' ? 'Brouillard de Guerre & Cartes' : 'Fog of War & Maps',
      changes: [
        { type: 'new', text: language === 'fr' ? 'Brouillard de guerre avec pinceau de révélation' : 'Fog of war with revelation brush tool' },
        { type: 'new', text: language === 'fr' ? 'Partage de vue joueur en temps réel via SSE' : 'Real-time player view sharing via SSE' },
        { type: 'improved', text: language === 'fr' ? 'Performances des cartes avec beaucoup de marqueurs' : 'Map performance with many markers' },
        { type: 'fixed', text: language === 'fr' ? 'Correction du zoom sur appareils tactiles' : 'Fixed zoom on touch devices' },
      ],
    },
  ];

  return (
    <PublicLayout>
      {/* Hero */}
      <section className="py-16 sm:py-24 px-4 text-center relative">
        <div className="absolute inset-0 bg-gradient-to-b from-primary/5 via-background to-background dark:from-primary/8" />
        <div className="relative z-10 max-w-3xl mx-auto">
          <h1 className="font-cinzel text-3xl sm:text-4xl lg:text-5xl font-bold mb-4">
            {t('updates_page_title', language)}
          </h1>
          <p className="text-muted-foreground text-base sm:text-lg">
            {t('updates_page_subtitle', language)}
          </p>
        </div>
      </section>

      {/* Timeline */}
      <section className="pb-20 sm:pb-28 px-4">
        <div className="max-w-3xl mx-auto relative">
          {/* Vertical line */}
          <div className="absolute left-[19px] top-0 bottom-0 w-px bg-border hidden sm:block" />

          <div className="space-y-8">
            {releases.map((release, i) => (
              <div key={i} className="relative sm:pl-12">
                {/* Dot on timeline */}
                <div className="absolute left-[12px] top-6 w-[15px] h-[15px] rounded-full border-2 border-primary bg-background hidden sm:block" />

                <Card className="border-border/50">
                  <CardContent className="p-5 sm:p-6">
                    <div className="flex flex-wrap items-center gap-3 mb-3">
                      <span className="font-mono text-xs px-2 py-1 rounded bg-primary/10 text-primary font-bold">
                        v{release.version}
                      </span>
                      <span className="text-xs text-muted-foreground">{release.date}</span>
                    </div>

                    <h3 className="font-cinzel font-bold text-lg mb-4">{release.title}</h3>

                    <ul className="space-y-2.5">
                      {release.changes.map((change, ci) => (
                        <li key={ci} className="flex items-start gap-3">
                          <ChangeBadge type={change.type} language={language} />
                          <span className="text-sm text-muted-foreground">{change.text}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              </div>
            ))}
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}
